import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
  Tabs,
  Tab,
  FormControlLabel,
} from "@mui/material";
import {
  FaEye,
  FaSearch,
  FaFileAlt,
  FaUndo,
  FaFileExcel,
  FaFilePdf,
  FaChartBar,
  FaChartPie,
  FaChartLine,
  FaTable,
  FaFilter,
  FaSort,
  FaSortAmountDown,
  FaSortAmountUp,
  FaColumns,
  FaSave,
} from "react-icons/fa";
import { DatePicker, Space } from "antd";
import { gbState } from "../../../components/shared/GlobalState";
import useFetch from "../../../components/shared/useFetch";
import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
} from "recharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Define chart colors
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
  "#FFC658",
  "#8DD1E1",
  "#A4DE6C",
  "#D0ED57",
];

const { RangePicker } = DatePicker;

// Define available columns for customization
const availableColumns = [
  { id: "receipt_number", label: "Receipt Number" },
  { id: "date", label: "Date" },
  { id: "income_title", label: "Title" },
  { id: "category_id.category_name", label: "Category" },
  { id: "amount", label: "Amount" },
  { id: "income_source", label: "Source" },
  { id: "financial_year", label: "Financial Year" },
  { id: "member_id.first_name", label: "Member" },
  { id: "other_non_member", label: "Non-Member" },
  { id: "description", label: "Description" },
];

// Define grouping options
const groupingOptions = [
  { value: "none", label: "No Grouping" },
  { value: "category", label: "By Category" },
  { value: "source", label: "By Source" },
  { value: "month", label: "By Month" },
  { value: "year", label: "By Year" },
  { value: "financial_year", label: "By Financial Year" },
];

// Define visualization types
const visualizationTypes = [
  { value: "table", label: "Table View", icon: <FaTable /> },
  { value: "bar", label: "Bar Chart", icon: <FaChartBar /> },
  { value: "pie", label: "Pie Chart", icon: <FaChartPie /> },
  { value: "line", label: "Line Chart", icon: <FaChartLine /> },
];

const IncomeFlexiReportsChild = () => {
  const navigate = useNavigate();
  const [{ user }] = useAtom(gbState);
  const [incomeData, setIncomeData] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Handle pie chart sector hover
  const onPieEnter = useCallback((_, index) => {
    setActiveIndex(index);
  }, []);

  // Custom pie chart active shape
  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <path
          d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
          stroke={fill}
          fill="none"
        />
        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          textAnchor={textAnchor}
          fill="#333"
        >{`$${value.toFixed(2)}`}</text>
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 12}
          y={ey}
          dy={18}
          textAnchor={textAnchor}
          fill="#999"
        >
          {`(${(percent * 100).toFixed(2)}%)`}
        </text>
      </g>
    );
  };

  // Report configuration state
  const [selectedColumns, setSelectedColumns] = useState([
    "receipt_number",
    "date",
    "income_title",
    "category_id.category_name",
    "amount",
    "income_source",
  ]);
  const [groupBy, setGroupBy] = useState("none");
  const [visualizationType, setVisualizationType] = useState("table");
  const [dateRange, setDateRange] = useState([null, null]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [sourceFilter, setSourceFilter] = useState([]);
  const [amountRange, setAmountRange] = useState([0, 10000]);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [configName, setConfigName] = useState("");
  const [savedConfigurations, setSavedConfigurations] = useState([]);

  // Fetch data
  const urlLink = `${process.env.REACT_APP_API_URL}/income/${user.church_id}`;
  const { data, loading, error, refetch } = useFetch(urlLink);

  // Extract unique categories and sources for filters
  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(
        incomeData
          .map((item) => item.category_id?.category_name)
          .filter(Boolean)
      ),
    ];
    return uniqueCategories;
  }, [incomeData]);

  const sources = useMemo(() => {
    const uniqueSources = [
      ...new Set(incomeData.map((item) => item.income_source).filter(Boolean)),
    ];
    return uniqueSources;
  }, [incomeData]);

  // Process data when received
  useEffect(() => {
    if (data?.incomes) {
      const updatedData = data.incomes.map((item) => ({
        ...item,
        amount: parseFloat(item.amount),
      }));
      setIncomeData(updatedData);
    }
  }, [data]);

  // Handle column selection
  const handleColumnChange = (event) => {
    const { value } = event.target;
    setSelectedColumns(value);
  };

  // Handle grouping change
  const handleGroupingChange = (event) => {
    setGroupBy(event.target.value);
  };

  // Handle visualization type change
  const handleVisualizationChange = (event, newValue) => {
    setVisualizationType(newValue);
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortBy === key && sortDirection === "asc") {
      direction = "desc";
    }
    setSortBy(key);
    setSortDirection(direction);
  };

  // Handle category filter
  const handleCategoryFilter = (event) => {
    setCategoryFilter(event.target.value);
  };

  // Handle source filter
  const handleSourceFilter = (event) => {
    setSourceFilter(event.target.value);
  };

  // Reset all filters
  const resetFilters = () => {
    setDateRange([null, null]);
    setSearchTerm("");
    setCategoryFilter([
      ...new Set(
        incomeData
          .map((item) => item.category_id?.category_name)
          .filter(Boolean)
      ),
    ]);
    setSourceFilter([
      ...new Set(incomeData.map((item) => item.income_source).filter(Boolean)),
    ]);
    setAmountRange([0, 10000]);
    setSortBy("date");
    setSortDirection("desc");
    setVisualizationType("table");
  };

  // Save configuration
  const saveConfiguration = () => {
    if (!configName) return;

    const newConfig = {
      name: configName,
      config: {
        dateRange,
        categoryFilter,
        sourceFilter,
        amountRange,
        selectedColumns,
        groupBy,
        sortBy,
        sortDirection,
        visualizationType,
      },
    };

    const updatedConfigs = [...savedConfigurations, newConfig];
    setSavedConfigurations(updatedConfigs);
    localStorage.setItem("incomeReportConfigs", JSON.stringify(updatedConfigs));
    setShowSaveModal(false);
    setConfigName("");
  };

  // Load configuration
  const loadConfiguration = (config) => {
    setDateRange(config.dateRange);
    setCategoryFilter(config.categoryFilter);
    setSourceFilter(config.sourceFilter);
    setAmountRange(config.amountRange);
    setSelectedColumns(config.selectedColumns);
    setGroupBy(config.groupBy);
    setSortBy(config.sortBy);
    setSortDirection(config.sortDirection);
    setVisualizationType(config.visualizationType);
  };

  // Load saved configurations from localStorage
  useEffect(() => {
    const savedConfigs = localStorage.getItem("incomeReportConfigs");
    if (savedConfigs) {
      setSavedConfigurations(JSON.parse(savedConfigs));
    }
  }, []);

  // Apply all filters and sorting to data
  const filteredAndSortedData = useMemo(() => {
    let result = [...incomeData];

    // Apply date range filter
    if (dateRange && dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      const endDate = new Date(dateRange[1]);
      result = result.filter((item) => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    // Apply category filter
    if (categoryFilter.length > 0) {
      result = result.filter((item) =>
        categoryFilter.includes(item.category_id?.category_name)
      );
    }

    // Apply source filter
    if (sourceFilter.length > 0) {
      result = result.filter((item) =>
        sourceFilter.includes(item.income_source)
      );
    }

    // Apply amount range filter
    if (amountRange[0] > 0 || amountRange[1] < 10000) {
      result = result.filter(
        (item) => item.amount >= amountRange[0] && item.amount <= amountRange[1]
      );
    }

    // Apply search term
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          (item.income_title &&
            item.income_title.toLowerCase().includes(lowercasedSearch)) ||
          (item.receipt_number &&
            item.receipt_number.toLowerCase().includes(lowercasedSearch)) ||
          (item.description &&
            item.description.toLowerCase().includes(lowercasedSearch)) ||
          (item.other_non_member &&
            item.other_non_member.toLowerCase().includes(lowercasedSearch)) ||
          (item.member_id?.first_name &&
            item.member_id.first_name
              .toLowerCase()
              .includes(lowercasedSearch)) ||
          (item.member_id?.last_name &&
            item.member_id.last_name.toLowerCase().includes(lowercasedSearch))
      );
    }

    // Apply sorting
    if (sortBy) {
      result.sort((a, b) => {
        // Handle nested properties like category_id.category_name
        const keyParts = sortBy.split(".");
        let aValue = a;
        let bValue = b;

        for (const part of keyParts) {
          aValue = aValue?.[part];
          bValue = bValue?.[part];
        }

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === "string") {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === "asc" ? comparison : -comparison;
        } else {
          return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
        }
      });
    }

    return result;
  }, [
    incomeData,
    dateRange,
    categoryFilter,
    sourceFilter,
    amountRange,
    searchTerm,
    sortBy,
    sortDirection,
  ]);

  // Group data if grouping is selected
  const groupedData = useMemo(() => {
    if (groupBy === "none") return filteredAndSortedData;

    const groups = {};

    filteredAndSortedData.forEach((item) => {
      let groupKey;

      switch (groupBy) {
        case "category":
          groupKey = item.category_id?.category_name || "Uncategorized";
          break;
        case "source":
          groupKey = item.income_source || "Unknown";
          break;
        case "month":
          groupKey = new Date(item.date).toLocaleString("default", {
            month: "long",
            year: "numeric",
          });
          break;
        case "year":
          groupKey = new Date(item.date).getFullYear().toString();
          break;
        case "financial_year":
          groupKey = item.financial_year || "Unknown";
          break;
        default:
          groupKey = "All";
      }

      if (!groups[groupKey]) {
        groups[groupKey] = {
          items: [],
          totalAmount: 0,
        };
      }

      groups[groupKey].items.push(item);
      groups[groupKey].totalAmount += item.amount;
    });

    return groups;
  }, [filteredAndSortedData, groupBy]);

  // Prepare chart data
  //   const chartData = useMemo(() => {
  //     if (groupBy === 'none') {
  //       // If no grouping, use category as default for charts
  //       const categoryGroups = {};

  //       filteredAndSortedData.forEach(item => {
  //         const category = item.category_id?.category_name || 'Uncategorized';

  //         if (!categoryGroups[category]) {
  //           categoryGroups[category] = 0;
  //         }

  //         categoryGroups[category] += item.amount;
  //       });

  //       const labels = Object.keys(categoryGroups);
  //       const data = Object.values(categoryGroups);

  //       return {
  //         labels,
  //         datasets: [
  //           {
  //             label: 'Income by Category',
  //             data,
  //             backgroundColor: [
  //               'rgba(255, 99, 132, 0.6)',
  //               'rgba(54, 162, 235, 0.6)',
  //               'rgba(255, 206, 86, 0.6)',
  //               'rgba(75, 192, 192, 0.6)',
  //               'rgba(153, 102, 255, 0.6)',
  //               'rgba(255, 159, 64, 0.6)',
  //               'rgba(199, 199, 199, 0.6)',
  //               'rgba(83, 102, 255, 0.6)',
  //               'rgba(40, 159, 64, 0.6)',
  //               'rgba(210, 199, 199, 0.6)',
  //             ],
  //             borderColor: [
  //               'rgba(255, 99, 132, 1)',
  //               'rgba(54, 162, 235, 1)',
  //               'rgba(255, 206, 86, 1)',
  //               'rgba(75, 192, 192, 1)',
  //               'rgba(153, 102, 255, 1)',
  //               'rgba(255, 159, 64, 1)',
  //               'rgba(199, 199, 199, 1)',
  //               'rgba(83, 102, 255, 1)',
  //               'rgba(40, 159, 64, 1)',
  //               'rgba(210, 199, 199, 1)',
  //             ],
  //             borderWidth: 1,
  //           },
  //         ],
  //       };
  //     } else {
  //       // Use the current grouping
  //       const labels = Object.keys(groupedData);
  //       const data = labels.map(label => groupedData[label].totalAmount);

  //       return {
  //         labels,
  //         datasets: [
  //           {
  //             label: `Income by ${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}`,
  //             data,
  //             backgroundColor: [
  //               'rgba(255, 99, 132, 0.6)',
  //               'rgba(54, 162, 235, 0.6)',
  //               'rgba(255, 206, 86, 0.6)',
  //               'rgba(75, 192, 192, 0.6)',
  //               'rgba(153, 102, 255, 0.6)',
  //               'rgba(255, 159, 64, 0.6)',
  //               'rgba(199, 199, 199, 0.6)',
  //               'rgba(83, 102, 255, 0.6)',
  //               'rgba(40, 159, 64, 0.6)',
  //               'rgba(210, 199, 199, 0.6)',
  //             ],
  //             borderColor: [
  //               'rgba(255, 99, 132, 1)',
  //               'rgba(54, 162, 235, 1)',
  //               'rgba(255, 206, 86, 1)',
  //               'rgba(75, 192, 192, 1)',
  //               'rgba(153, 102, 255, 1)',
  //               'rgba(255, 159, 64, 1)',
  //               'rgba(199, 199, 199, 1)',
  //               'rgba(83, 102, 255, 1)',
  //               'rgba(40, 159, 64, 1)',
  //               'rgba(210, 199, 199, 1)',
  //             ],
  //             borderWidth: 1,
  //           },
  //         ],
  //       };
  //     }
  //   }, [filteredAndSortedData, groupedData, groupBy]);

  // Line chart data (special case for time series)
  const lineChartData = useMemo(() => {
    // Group by month for line chart
    const monthlyData = {};

    filteredAndSortedData.forEach((item) => {
      const date = new Date(item.date);
      const monthYear = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}`;

      if (!monthlyData[monthYear]) {
        monthlyData[monthYear] = 0;
      }

      monthlyData[monthYear] += item.amount;
    });

    // Sort by date
    const sortedMonths = Object.keys(monthlyData).sort();

    return {
      labels: sortedMonths.map((month) => {
        const [year, monthNum] = month.split("-");
        return `${new Date(0, parseInt(monthNum) - 1).toLocaleString(
          "default",
          { month: "short" }
        )} ${year}`;
      }),
      datasets: [
        {
          label: "Income Trend",
          data: sortedMonths.map((month) => monthlyData[month]),
          fill: false,
          backgroundColor: "rgba(75, 192, 192, 0.6)",
          borderColor: "rgba(75, 192, 192, 1)",
          tension: 0.1,
        },
      ],
    };
  }, [filteredAndSortedData]);

  // Export to Excel
  const exportToExcel = () => {
    let dataToExport = [];

    if (groupBy === "none") {
      // Export flat data
      dataToExport = filteredAndSortedData.map((item) => {
        const row = {};
        selectedColumns.forEach((col) => {
          const keyParts = col.split(".");
          let value = item;

          for (const part of keyParts) {
            value = value?.[part];
          }

          const columnDef = availableColumns.find((c) => c.id === col);
          row[columnDef?.label || col] = value;
        });
        return row;
      });
    } else {
      // Export grouped data
      Object.keys(groupedData).forEach((groupName) => {
        const group = groupedData[groupName];

        // Add a row for the group header with total
        dataToExport.push({
          [`${groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}`]: groupName,
          "Total Amount": group.totalAmount,
        });

        // Add rows for each item in the group
        group.items.forEach((item) => {
          const row = {};
          selectedColumns.forEach((col) => {
            const keyParts = col.split(".");
            let value = item;

            for (const part of keyParts) {
              value = value?.[part];
            }

            const columnDef = availableColumns.find((c) => c.id === col);
            row[columnDef?.label || col] = value;
          });
          dataToExport.push(row);
        });

        // Add an empty row after each group
        dataToExport.push({});
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Income Report");

    // Generate filename with current date
    const date = new Date().toISOString().slice(0, 10);
    const fileName = `Income_Report_${date}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(18);
    doc.text("Income Report", 14, 22);

    // Add date
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    if (visualizationType !== "table") {
      // Add chart image (this would require canvas conversion)
      doc.text(
        "Chart visualization export not supported in this version",
        14,
        40
      );
    }

    // Add table data
    const tableColumn = selectedColumns.map((col) => {
      const columnDef = availableColumns.find((c) => c.id === col);
      return columnDef?.label || col;
    });

    let tableRows = [];

    if (groupBy === "none") {
      // Flat data
      tableRows = filteredAndSortedData.map((item) => {
        return selectedColumns.map((col) => {
          const keyParts = col.split(".");
          let value = item;

          for (const part of keyParts) {
            value = value?.[part];
          }

          return value !== null && value !== undefined ? value.toString() : "";
        });
      });
    } else {
      // Grouped data
      Object.keys(groupedData).forEach((groupName) => {
        const group = groupedData[groupName];

        // Add a row for the group header with total
        tableRows.push([`${groupName} (Total: ${group.totalAmount})`]);

        // Add rows for each item in the group
        group.items.forEach((item) => {
          tableRows.push(
            selectedColumns.map((col) => {
              const keyParts = col.split(".");
              let value = item;

              for (const part of keyParts) {
                value = value?.[part];
              }

              return value !== null && value !== undefined
                ? value.toString()
                : "";
            })
          );
        });
      });
    }

    // Use autoTable directly instead of doc.autoTable
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8 },
    });

    // Generate filename with current date
    const date = new Date().toISOString().slice(0, 10);
    const fileName = `Income_Report_${date}.pdf`;

    doc.save(fileName);
  };

  // Calculate total amount
  const totalAmount = useMemo(() => {
    return filteredAndSortedData.reduce((sum, item) => sum + item.amount, 0);
  }, [filteredAndSortedData]);

  // Prepare data for charts
  const chartData = useMemo(() => {
    if (groupBy !== "none") {
      // Use the grouped data for charts
      return Object.entries(groupedData).map(([groupName, group]) => ({
        name: groupName,
        value: group.totalAmount,
        count: group.items.length,
      }));
    } else {
      // If not grouped, group by category for charts
      const categoryGroups = {};
      filteredAndSortedData.forEach((income) => {
        const category = income.category_id?.category_name || "Uncategorized";
        if (!categoryGroups[category]) {
          categoryGroups[category] = {
            name: category,
            value: 0,
            count: 0,
          };
        }
        categoryGroups[category].value += income.amount;
        categoryGroups[category].count += 1;
      });

      return Object.values(categoryGroups);
    }
  }, [filteredAndSortedData, groupedData, groupBy]);

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-t-2 border-b-2 border-blue-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div
        className="relative px-4 py-3 text-red-700 bg-red-100 border border-red-400 rounded"
        role="alert"
      >
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline">
          {" "}
          {error.message || "Failed to load income data"}
        </span>
      </div>
    );
  }

  return (
    <div className="w-full p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          Income Flexible Reports
        </h2>
        <div className="flex space-x-2">
          <Button
            variant="outlined"
            startIcon={<FaFilter />}
            onClick={() => setShowFilters(!showFilters)}
            className="text-blue-500 border-blue-500 hover:bg-blue-50"
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaColumns />}
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="text-purple-500 border-purple-500 hover:bg-purple-50"
          >
            Columns
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaSave />}
            onClick={() => setShowSaveModal(true)}
            className="text-green-500 border-green-500 hover:bg-green-50"
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<FaUndo />}
            onClick={resetFilters}
            className="text-red-500 border-red-500 hover:bg-red-50"
          >
            Reset
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="p-4 mb-6 rounded-lg bg-gray-50">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* First Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Date Range
              </p>
              <DatePicker.RangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full"
                format="MM-DD-YYYY"
              />
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Amount Range
              </p>
              <div className="flex items-center space-x-2">
                <TextField
                  type="number"
                  value={amountRange[0]}
                  onChange={(e) =>
                    setAmountRange([Number(e.target.value), amountRange[1]])
                  }
                  size="small"
                  className="w-1/2"
                />
                <span>to</span>
                <TextField
                  type="number"
                  value={amountRange[1]}
                  onChange={(e) =>
                    setAmountRange([amountRange[0], Number(e.target.value)])
                  }
                  size="small"
                  className="w-1/2"
                />
              </div>
            </div>

            {/* Second Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">
                Categories
              </p>
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  value={categoryFilter}
                  onChange={handleCategoryFilter}
                  renderValue={(selected) => selected.join(", ")}
                  displayEmpty
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      <Checkbox
                        checked={categoryFilter.indexOf(category) > -1}
                      />
                      <span>{category}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">Sources</p>
              <FormControl size="small" fullWidth>
                <Select
                  multiple
                  value={sourceFilter}
                  onChange={handleSourceFilter}
                  renderValue={(selected) => selected.join(", ")}
                  displayEmpty
                >
                  {sources.map((source) => (
                    <MenuItem key={source} value={source}>
                      <Checkbox checked={sourceFilter.indexOf(source) > -1} />
                      <span>{source}</span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Third Row - 2 columns */}
            <div>
              <p className="mb-1 text-sm font-medium text-gray-700">Group By</p>
              <FormControl size="small" fullWidth>
                <Select
                  value={groupBy}
                  onChange={handleGroupingChange}
                  displayEmpty
                >
                  {groupingOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Sort By
                </p>
                <FormControl size="small" fullWidth>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="date">Date</MenuItem>
                    <MenuItem value="amount">Amount</MenuItem>
                    <MenuItem value="income_title">Title</MenuItem>
                    <MenuItem value="category_id.category_name">
                      Category
                    </MenuItem>
                    {groupBy !== "none" && (
                      <MenuItem value="totalAmount">Total Amount</MenuItem>
                    )}
                    {groupBy !== "none" && (
                      <MenuItem value="count">Count</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </div>

              <div>
                <p className="mb-1 text-sm font-medium text-gray-700">
                  Sort Direction
                </p>
                <FormControl size="small" fullWidth>
                  <Select
                    value={sortDirection}
                    onChange={(e) => setSortDirection(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Column Selector */}
      {showColumnSelector && (
        <div className="p-4 mb-6 rounded-lg bg-gray-50">
          <p className="mb-2 text-sm font-medium text-gray-700">
            Select Columns to Display
          </p>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {availableColumns.map((column) => (
              <FormControlLabel
                key={column.id}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(column.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedColumns([...selectedColumns, column.id]);
                      } else {
                        setSelectedColumns(
                          selectedColumns.filter((col) => col !== column.id)
                        );
                      }
                    }}
                  />
                }
                label={column.label}
              />
            ))}
          </div>
        </div>
      )}

      {/* View Mode Selector */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-2">
          <Button
            variant={visualizationType === "table" ? "contained" : "outlined"}
            onClick={() => setVisualizationType("table")}
            className={visualizationType === "table" ? "bg-blue-600" : ""}
          >
            Table
          </Button>
          <Button
            variant={visualizationType === "bar" ? "contained" : "outlined"}
            onClick={() => setVisualizationType("bar")}
            className={visualizationType === "bar" ? "bg-blue-600" : ""}
          >
            Bar Chart
          </Button>
          <Button
            variant={visualizationType === "pie" ? "contained" : "outlined"}
            onClick={() => setVisualizationType("pie")}
            className={visualizationType === "pie" ? "bg-blue-600" : ""}
          >
            Pie Chart
          </Button>
        </div>

        <div className="flex space-x-2">
          <Button
            variant="contained"
            startIcon={<FaFilePdf />}
            onClick={exportToPDF}
            className="bg-red-600 hover:bg-red-700"
          >
            PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<FaFileExcel />}
            onClick={exportToExcel}
            className="bg-green-600 hover:bg-green-700"
          >
            Excel
          </Button>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="p-4 mb-6 rounded-lg bg-blue-50">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Total Income</p>
            <p className="text-xl font-semibold">${totalAmount.toFixed(2)}</p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Average</p>
            <p className="text-xl font-semibold">
              $
              {filteredAndSortedData.length > 0
                ? (totalAmount / filteredAndSortedData.length).toFixed(2)
                : "0.00"}
            </p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Maximum</p>
            <p className="text-xl font-semibold">
              $
              {filteredAndSortedData.length > 0
                ? Math.max(
                    ...filteredAndSortedData.map((item) => item.amount)
                  ).toFixed(2)
                : "0.00"}
            </p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Minimum</p>
            <p className="text-xl font-semibold">
              $
              {filteredAndSortedData.length > 0
                ? Math.min(
                    ...filteredAndSortedData.map((item) => item.amount)
                  ).toFixed(2)
                : "0.00"}
            </p>
          </div>
          <div className="p-3 bg-white rounded-md shadow-sm">
            <p className="text-xs text-gray-500">Count</p>
            <p className="text-xl font-semibold">
              {filteredAndSortedData.length}
            </p>
          </div>
        </div>
      </div>

      {/* Save Configuration Modal */}
      {showSaveModal && (
        <Modal
          title="Save Report Configuration"
          onClose={() => setShowSaveModal(false)}
          width="400px"
        >
          <div className="p-4">
            <TextField
              label="Configuration Name"
              value={configName}
              onChange={(e) => setConfigName(e.target.value)}
              fullWidth
              className="mb-4"
            />
            <div className="flex justify-end space-x-2">
              <Button
                variant="outlined"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={saveConfiguration}
                disabled={!configName}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Visualization Content */}
      <div className="w-full bg-white border border-gray-200 rounded-lg">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">Error loading data</div>
        ) : (
          <>
            {/* Table View */}
            {visualizationType === "table" && (
              <TableContainer
                component={Paper}
                className="max-h-[600px] w-full"
              >
                <Table stickyHeader className="w-full">
                  <TableHead>
                    <TableRow>
                      {groupBy !== "none" && (
                        <TableCell className="font-semibold bg-gray-100">
                          {groupBy}
                        </TableCell>
                      )}
                      {groupBy !== "none" && (
                        <TableCell className="font-semibold bg-gray-100">
                          Count
                        </TableCell>
                      )}
                      {groupBy !== "none" && (
                        <TableCell className="font-semibold bg-gray-100">
                          Total Amount
                        </TableCell>
                      )}

                      {groupBy === "none" &&
                        selectedColumns.map((column) => {
                          const columnDef = availableColumns.find(
                            (c) => c.id === column
                          );
                          return (
                            <TableCell
                              key={column}
                              className="font-semibold bg-gray-100"
                            >
                              {columnDef?.label || column}
                              <IconButton
                                size="small"
                                onClick={() => handleSort(column)}
                              >
                                <FaSortAmountDown
                                  className={
                                    sortBy === column
                                      ? "text-blue-500"
                                      : "text-gray-400"
                                  }
                                />
                              </IconButton>
                            </TableCell>
                          );
                        })}
                      <TableCell className="font-semibold bg-gray-100">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupBy !== "none"
                      ? // Grouped data
                        Object.entries(groupedData).map(
                          ([groupName, group], index) => (
                            <TableRow
                              key={groupName}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }
                            >
                              <TableCell>{groupName}</TableCell>
                              <TableCell>{group.items.length}</TableCell>
                              <TableCell>
                                ${group.totalAmount.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <IconButton size="small" color="primary">
                                  <FaEye />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )
                        )
                      : // Flat data
                        filteredAndSortedData.map((row, index) => (
                          <TableRow
                            key={index}
                            className={
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            }
                          >
                            {selectedColumns.map((column) => {
                              // Handle nested properties
                              const keyParts = column.split(".");
                              let value = row;

                              for (const part of keyParts) {
                                value = value?.[part];
                              }

                              // Format based on column type
                              if (column === "date") {
                                return (
                                  <TableCell key={column}>
                                    {value
                                      ? (() => {
                                          const date = new Date(value);
                                          const month = String(
                                            date.getMonth() + 1
                                          ).padStart(2, "0");
                                          const day = String(
                                            date.getDate()
                                          ).padStart(2, "0");
                                          const year = date.getFullYear();
                                          return `${month}-${day}-${year}`;
                                        })()
                                      : ""}
                                  </TableCell>
                                );
                              } else if (column === "amount") {
                                return (
                                  <TableCell key={column}>
                                    $
                                    {value?.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }) || "0.00"}
                                  </TableCell>
                                );
                              } else {
                                return (
                                  <TableCell key={column}>
                                    {value || ""}
                                  </TableCell>
                                );
                              }
                            })}
                            <TableCell>
                              <IconButton size="small" color="primary">
                                <FaEye />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Chart Views */}
            {visualizationType === "bar" && (
              <div className="p-4 h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" fill="#8884d8" name="Amount" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {visualizationType === "pie" && (
              <div className="p-4 h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      activeIndex={activeIndex}
                      activeShape={renderActiveShape}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      onMouseEnter={onPieEnter}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default IncomeFlexiReportsChild;
